import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
} from '@chakra-ui/react';
import { Composition } from '@texas/api/endpoints/compositionApi';
import { ColorDot } from '@texas/components/shared/colorPicker/ColorDot';
import { usePortalRefValue } from '@texas/hooks/usePortalRef';
import { useTranslation } from 'react-i18next';
import { CompositionName } from '../../options/shared/CompositionName';

interface Props {
  compositions: Composition[];
}

export function VersionMaterialsSummary({ compositions }: Props) {
  const { t } = useTranslation();
  const { ref } = usePortalRefValue();
  const materials = compositions.flatMap(
    (composition) => composition.materials,
  );
  const colors = Array.from(
    new Map(
      materials.flatMap((material) =>
        material.colors.map(
          (color) =>
            [`${color.code}-${color.hex}-${color.name}`, color] as const,
        ),
      ),
    ).values(),
  );

  if (materials.length === 0)
    return <Text color="entityDirectory.mutedText">—</Text>;

  return (
    <Popover placement="bottom-start" isLazy={true}>
      <PopoverTrigger>
        <Button
          variant="unstyled"
          h="auto"
          minW="160px"
          maxW="220px"
          display="block"
          textAlign="left"
          whiteSpace="normal"
          fontWeight="normal"
          aria-label={t('general.materials')}
        >
          <Text
            as="span"
            display="block"
            fontSize="12px"
            fontWeight="semibold"
            noOfLines={2}
          >
            {materials[0].material.text}
          </Text>
          <Flex as="span" gap={1.5} mt={1} align="center" wrap="wrap">
            {colors.slice(0, 3).map((color) => (
              <ColorDot
                key={`${color.code}-${color.hex}-${color.name}`}
                hex={color.hex}
                code={color.code}
                name={color.name}
              />
            ))}
            <Text fontSize="11px" color="entityDirectory.mutedText">
              {materials.length > 1
                ? t('compositionGroup.version.additionalMaterials', {
                    count: materials.length - 1,
                  })
                : colors.length > 0
                ? t('compositionGroup.version.colorCount', {
                    count: colors.length,
                  })
                : null}
            </Text>
          </Flex>
        </Button>
      </PopoverTrigger>
      <Portal containerRef={ref}>
        <PopoverContent w="360px" maxW="calc(100vw - 32px)">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontWeight="semibold">
            {t('general.materials')}
          </PopoverHeader>
          <PopoverBody maxH="360px" overflowY="auto">
            <Flex direction="column" gap={4}>
              {compositions
                .filter((composition) => composition.materials.length > 0)
                .map((composition) => (
                  <Box key={composition.id}>
                    <CompositionName
                      note={composition.note}
                      index={compositions.indexOf(composition) + 1}
                    />
                    {composition.materials.map((material) => (
                      <Box key={material.id} mt={2}>
                        <Text fontSize="sm" fontWeight="semibold">
                          {material.material.text}
                        </Text>
                        {material.qualities.length > 0 && (
                          <Text fontSize="xs">
                            <Text as="span" color="entityDirectory.mutedText">
                              {t('configuration.quality')}:{' '}
                            </Text>
                            {material.qualities
                              .map((quality) =>
                                [quality.text, quality.value]
                                  .filter(Boolean)
                                  .join(' '),
                              )
                              .join(', ')}
                          </Text>
                        )}
                        {material.treatments.length > 0 && (
                          <Text fontSize="xs">
                            <Text as="span" color="entityDirectory.mutedText">
                              {t('configuration.treatments')}:{' '}
                            </Text>
                            {material.treatments
                              .map((treatment) => treatment.text)
                              .join(', ')}
                          </Text>
                        )}
                        {material.techniques.length > 0 && (
                          <Text fontSize="xs">
                            <Text as="span" color="entityDirectory.mutedText">
                              {t('configuration.techniques')}:{' '}
                            </Text>
                            {material.techniques
                              .map((technique) => technique.text)
                              .join(', ')}
                          </Text>
                        )}
                        <Flex gap={1.5} mt={1} wrap="wrap">
                          {material.colors.map((color, index) => (
                            <ColorDot
                              key={index}
                              hex={color.hex}
                              code={color.code}
                              name={color.name}
                            />
                          ))}
                        </Flex>
                      </Box>
                    ))}
                  </Box>
                ))}
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
